import { AssignRepToContacts, EditContact, GetContact, RegisterContact } from "@/dtos/Contact.dto";
import { StateObject } from "@/dtos/State.dto";
import Contact from "@/services/contact/contact";
import {
  ADD_CONTACT_PHONE_NUMBER,
  ASSIGN_REP_TO_CONTACTS,
  CLEAR_CONTACT_ISLOADING,
  DELETE_CONTACT,
  DELETE_CONTACT_PHONE_NUMBER,
  EDIT_CONTACT,
  EDIT_CONTACT_PHONE_NUMBER,
  GET_CONTACT,
  GET_CONTACTS,
  IMPORT_CONTACT,
  REGISTER_CONTACT,
} from "@/store/action-types";
import { SET_CONTACT, SET_CONTACTS, SET_ERRORMSG } from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const contact: Module<any, {}> = {
  state: {
    contact: {
      data: null,
      isLoading: false,
    },
    contacts: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    contact: (state: { contact: any }) => state.contact,
    contacts: (state: { contacts: any }) => state.contacts,
  },
  mutations: {
    [SET_CONTACT]: (
      state: { contact: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.contact.data = payload.data;
      state.contact.isLoading = payload.isLoading;
    },
    [SET_CONTACTS]: (
      state: { contacts: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.contacts.data = payload.data;
      if (payload.metaData != null) state.contacts.metaData = payload.metaData;
      state.contacts.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_CONTACT]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.registerContact(payload as RegisterContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to register contact");
        }
        return false;
      }
    },
    [IMPORT_CONTACT]: async ({ commit }: any, payload: FormData) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.importContact(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(
            SET_ERRORMSG,
            "Import request timed out. Please check your connection and try again."
          );
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to import contacts");
        }
        return false;
      }
    },
    [GET_CONTACTS]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACTS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.getContacts(payload as GetContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACTS, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_CONTACTS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACTS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACTS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load contacts");
        }
        return false;
      }
    },
    [GET_CONTACT]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.getContacts(payload as GetContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data[0], isLoading: false });
            return result.data.data[0];
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load contact");
        }
        return false;
      }
    },
    [EDIT_CONTACT]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.editContact(payload as EditContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit contact");
        }
        return false;
      }
    },
    [ADD_CONTACT_PHONE_NUMBER]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.addContactPhoneNumber(payload as EditContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return result.data.data;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to add phone number");
        }
        return false;
      }
    },
    [EDIT_CONTACT_PHONE_NUMBER]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.editContactPhoneNumber(payload as EditContact);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return result.data.data;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit phone number");
        }
        return false;
      }
    },
    [DELETE_CONTACT_PHONE_NUMBER]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.deleteContactPhoneNumber(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return result.data.data;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete phone number");
        }
        return false;
      }
    },
    [DELETE_CONTACT]: async ({ commit }: any, payload: string) => {
      commit(SET_CONTACT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Contact.deleteContact(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_CONTACT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete contact");
        }
        return false;
      }
    },
    [ASSIGN_REP_TO_CONTACTS]: async ({ commit }: any, payload: AssignRepToContacts) => {
      commit(SET_CONTACTS, { isLoading: true });

      try {
        const result: AxiosResponse = await Contact.assignRepToContacts(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACTS, { isLoading: false });
            return true;
          }
          commit(SET_CONTACTS, { isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACTS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACTS, { isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to assign rep to contacts");
        }
        return false;
      }
    },
    [CLEAR_CONTACT_ISLOADING]: ({ commit }) => {
      commit(SET_CONTACT, { isLoading: false });
      commit(SET_CONTACTS, { isLoading: false });
    },
  },
};

export default contact;
